import { useState, useEffect, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchProductStoreProductsAction,
  resetProductStoreProducts,
} from "../slices/productStoreProductsSlice";
import { RootState } from "@/app/rootReducer";
import { FetchProductStoreProductsRequest } from "../api/fetchProductStoreProducts/types";
import { PaginationState } from "@/components/Datatable/components/DataTablePagination/types";
import { OnChangeFn, SortingState } from "@/components/Datatable/types";

export function useProductStoreProducts(refreshTrigger?: number) {
  const dispatch = useDispatch();
  const { products, totalCount, loading, error } = useSelector(
    (state: RootState) => state.productStoreProducts,
  );

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });

  const [sorting, setSorting] = useState<SortingState>([]);

  const [filters, setFilters] = useState<{
    searchTerm?: string;
    isActive: number;
  }>({
    searchTerm: "",
    isActive: 1,
  });

  const requestData: FetchProductStoreProductsRequest = useMemo(
    () => ({
      page: pagination.pageIndex + 1,
      sortBy:
        sorting.length > 0 && ["name", "code", "price"].includes(sorting[0].id)
          ? (sorting[0].id as "name" | "code" | "price")
          : undefined,
      sortOrder:
        sorting.length > 0 ? (sorting[0].desc ? "DESC" : "ASC") : undefined,
      searchTerm: filters.searchTerm,
      pageSize: pagination.pageSize,
      isActive: filters.isActive,
    }),
    [pagination, sorting, filters],
  );

  useEffect(() => {
    dispatch(fetchProductStoreProductsAction(requestData));
  }, [dispatch, requestData, refreshTrigger]);

  useEffect(() => {
    return () => {
      dispatch(resetProductStoreProducts());
    };
  }, [dispatch]);

  const handlePaginationChange: OnChangeFn<PaginationState> = useCallback(
    (updaterOrValue) => {
      setPagination((old) =>
        typeof updaterOrValue === "function"
          ? updaterOrValue(old)
          : updaterOrValue,
      );
    },
    [],
  );

  const handleSortingChange: OnChangeFn<SortingState> = useCallback(
    (updaterOrValue) => {
      setSorting((old) =>
        typeof updaterOrValue === "function"
          ? updaterOrValue(old)
          : updaterOrValue,
      );
    },
    [],
  );

  const handleFilterChange = useCallback(
    (searchTerm: string, isActive?: number) => {
      setFilters((prev) => ({
        searchTerm,
        isActive: isActive !== undefined ? isActive : prev.isActive,
      }));
      setPagination((prev) => ({
        ...prev,
        pageIndex: 0,
      }));
    },
    [],
  );

  const refetch = useCallback(() => {
    dispatch(fetchProductStoreProductsAction(requestData));
  }, [dispatch, requestData]);

  return {
    products,
    totalCount,
    loading,
    error,
    pagination,
    sorting,
    filters,
    handlePaginationChange,
    handleSortingChange,
    handleFilterChange,
    refetch,
  };
}
